import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { TranslationService } from '../../services/translation.service';
import {
  DOMAIN_MATRIX,
  DomainMatrix,
  TOOLS_BY_ID,
  ToolDef,
  toolImgUrl,
} from './tools.data';
import { RoutineStoreService } from './routine-store.service';
import { RoutineService } from '../../services/routine.service';
import { RestService } from '../../services/rest.service';
import { RoutineSchedule, TOOL_BY_ID } from '../../shell/shell.models';
import { DismissedToolsService } from '../../shell/dismissed-tools.service';
import { RatedSymptom, toolsForSymptom, topRatedSymptom } from '../../shell/tool-reason';
import { AppDownloadCardComponent } from '../../shared/components/app-download/app-download-card.component';
import { isWebDailyLoop } from '../../services/daily-loop';

interface GridTool extends ToolDef {
  img: string;
  /** the category rows this tool is recommended in */
  cats: string[];
}

/**
 * The tool suggestion at the end of each results section (symptoms / THINK /
 * SENSE).
 *
 * Revital brief M5 (Danna 23.09, "build the best fit"): the section used to end
 * with a grid of up to fourteen tools, all with equal weight. Now it leads with
 * ONE "#1 best fit" activity: title, outcome line, duration, a reason drawn from
 * her own ratings, "add to my routine", an information sheet and "not for me".
 * Everything else sits behind "explore more tools", filterable by category.
 *
 * "Not for me" shares the home screen's decline list, and follows Danna's 22.09
 * rule: the card never vanishes on the spot. It quietens, "Not for me" stays
 * chosen and a second tap cancels it, and
 * the next visit simply suggests the next tool.
 */
@Component({
  selector: 'app-tools-recommendation',
  standalone: true,
  imports: [CommonModule, TranslatePipe, AppDownloadCardComponent],
  template: `
    <div *ngIf="matrix && best" class="card bf-wrap">
      <div class="bf-eyebrow">{{ 'results.tools.fit.eyebrow' | translate }}</div>

      <div class="bf-card" [class.bf-declined]="declined">
        <div class="bf-top">
          <img class="bf-img" [src]="best.img" alt="" [style.background]="best.bg" />
          <div class="bf-head">
            <div class="bf-name">{{ best.labelKey | translate }}</div>
            <div class="bf-desc">{{ best.descKey | translate }}</div>
            <div class="bf-mins" *ngIf="mins(best.id)">
              {{ 'shell.home.minutes' | translate: { n: mins(best.id) } }}
            </div>
          </div>
        </div>

        <div class="bf-why" *ngIf="why">{{ why }}</div>

        <ng-container *ngIf="!declined; else declinedTpl">
          <button
            type="button"
            class="bf-add"
            [class.bf-added]="inRoutine(best.id)"
            [disabled]="busy || inRoutine(best.id)"
            (click)="addBest()"
          >
            <svg *ngIf="inRoutine(best.id)" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12" /></svg>
            {{ (inRoutine(best.id) ? 'results.tools.fit.inRoutine' : 'results.tools.fit.add') | translate }}
          </button>
          <div class="bf-links">
            <button type="button" class="bf-link" (click)="infoOpen = true">
              {{ 'results.tools.fit.info' | translate }}
            </button>
            <button
              type="button"
              class="bf-link bf-quiet"
              *ngIf="inRoutine(best.id)"
              [disabled]="busy"
              (click)="removeIds([best.id])"
            >
              {{ 'results.tools.fit.remove' | translate }}
            </button>
            <button type="button" class="bf-link bf-quiet" *ngIf="!inRoutine(best.id)" (click)="decline()">
              {{ 'shell.home.rec.noThanks' | translate }}
            </button>
          </div>
        </ng-container>
        <ng-template #declinedTpl>
          <!-- "Not for me" stays as a chosen option she can simply tap again to
               cancel, no separate undo (Danna 26.09). -->
          <div class="bf-declined-row">
            <button type="button" class="bf-not-chip" aria-pressed="true" (click)="undoDecline()">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
              {{ 'shell.home.rec.noThanks' | translate }}
            </button>
            <span>{{ 'results.tools.fit.declined' | translate }}</span>
          </div>
        </ng-template>
      </div>

      <button
        type="button"
        class="bf-more-toggle"
        (click)="moreOpen = !moreOpen"
        [attr.aria-expanded]="moreOpen"
      >
        {{ (moreOpen ? 'results.tools.fit.less' : 'results.tools.fit.more') | translate }}
        <span aria-hidden="true" class="bf-chev" [class.open]="moreOpen">&#8964;</span>
      </button>

      <div class="bf-more" *ngIf="moreOpen">
        <div class="bf-chips" *ngIf="matrix.categories.length > 1">
          <button type="button" class="bf-chip" [class.on]="!catFilter" (click)="catFilter = ''">
            {{ 'results.tools.fit.all' | translate }}
          </button>
          <button
            type="button"
            class="bf-chip"
            *ngFor="let c of matrix.categories"
            [class.on]="catFilter === c.key"
            (click)="catFilter = c.key"
          >
            {{ c.labelKey | translate }}
          </button>
        </div>

        <div class="bf-grid">
          <button
            type="button"
            class="bf-tool"
            *ngFor="let t of visibleTools"
            [class.on]="selected.has(t.id)"
            [class.has]="inRoutine(t.id)"
            [disabled]="busy"
            [attr.aria-pressed]="selected.has(t.id) || inRoutine(t.id)"
            (click)="toggle(t.id)"
          >
            <span class="bf-tool-img" [style.background]="t.bg">
              <img [src]="t.img" alt="" />
              <span class="bf-tick" *ngIf="selected.has(t.id) || inRoutine(t.id)" aria-hidden="true">&#10003;</span>
            </span>
            <span class="bf-tool-name">{{ t.labelKey | translate }}</span>
          </button>
        </div>

        <button
          type="button"
          class="bf-add bf-add-more"
          *ngIf="selected.size"
          [disabled]="busy"
          (click)="addSelected()"
        >
          {{ 'results.tools.rec.cta' | translate }}
        </button>
      </div>

      <p class="bf-note">{{ (webLoop ? 'results.tools.rec.noteWeb' : 'results.tools.rec.note') | translate }}</p>

      <!-- On the web, the moment she adds a tool the way to the app sits right
           here, where the routine was built (Danna 26.09). The component renders
           nothing inside the installed app. -->
      <div class="bf-app" *ngIf="addedHere">
        <app-download-card variant="row" placement="routine"></app-download-card>
      </div>
    </div>

    <!-- Information sheet for the best fit -->
    <div #bfOverlay class="bf-overlay" *ngIf="infoOpen && best" (click)="infoOpen = false">
      <div class="bf-sheet" role="dialog" aria-modal="true" (click)="$event.stopPropagation()">
        <div class="bf-sheet-head">
          <img class="bf-img" [src]="best.img" alt="" [style.background]="best.bg" />
          <div class="bf-name">{{ best.labelKey | translate }}</div>
        </div>

        <div class="bf-sec">
          <div class="bf-sec-t">{{ 'results.tools.fit.whatTitle' | translate }}</div>
          <p>{{ 'shell.tools.' + best.id + '.intro' | translate }}</p>
        </div>
        <div class="bf-sec">
          <div class="bf-sec-t">{{ 'results.tools.fit.whyTitle' | translate }}</div>
          <p>{{ why || (matrix!.titleKey | translate) }}</p>
        </div>
        <div class="bf-sec" *ngIf="mins(best.id)">
          <div class="bf-sec-t">{{ 'results.tools.fit.durationTitle' | translate }}</div>
          <p>{{ 'shell.home.minutes' | translate: { n: mins(best.id) } }}</p>
        </div>
        <div class="bf-sec">
          <div class="bf-sec-t">{{ 'results.tools.fit.needTitle' | translate }}</div>
          <p>{{ 'results.tools.fit.env.' + best.id | translate }}</p>
        </div>
        <div class="bf-sec">
          <div class="bf-sec-t">{{ 'results.tools.fit.accessTitle' | translate }}</div>
          <p>{{ 'results.tools.fit.access.' + best.id | translate }}</p>
        </div>
        <p class="bf-wellness">{{ 'results.tools.fit.wellness' | translate }}</p>

        <button type="button" class="bf-add" (click)="infoOpen = false">
          {{ 'results.tools.fit.close' | translate }}
        </button>
      </div>
    </div>
  `,
  styles: [
    `
      .bf-wrap { margin-top: 12px; margin-bottom: 12px; padding: 16px 18px 18px; }
      .bf-eyebrow {
        font-size: 11px; font-weight: 700; color: var(--pk); text-transform: uppercase;
        letter-spacing: 0.07em; margin-bottom: 10px;
      }
      .bf-card {
        border: 1px solid var(--g100, #eceaf2); border-radius: 14px; padding: 14px;
        background: #fff; transition: opacity 0.2s ease;
      }
      .bf-card.bf-declined { opacity: 0.6; }
      .bf-top { display: flex; gap: 12px; align-items: flex-start; }
      .bf-img { width: 64px; height: 64px; border-radius: 14px; object-fit: cover; flex-shrink: 0; display: block; }
      .bf-head { min-width: 0; }
      .bf-name { font-size: 16px; font-weight: 700; color: var(--navy, #1a1a2e); line-height: 1.3; }
      .bf-desc { font-size: 13px; color: var(--g600, #555); line-height: 1.5; margin-top: 2px; }
      .bf-mins { font-size: 12px; color: var(--g500, #666); margin-top: 4px; }
      .bf-why {
        margin-top: 10px; font-size: 13px; color: var(--navy, #1a1a2e); line-height: 1.5;
        background: #f7f3f8; border-radius: 10px; padding: 8px 10px;
      }
      .bf-add {
        width: 100%; margin-top: 12px; min-height: 44px; padding: 10px 12px; border: none; border-radius: 12px;
        background: var(--brand-fill-safe, var(--brand-color, #d1409c)); color: #fff;
        font-size: 14px; font-weight: 600; font-family: inherit; cursor: pointer;
        display: flex; align-items: center; justify-content: center; gap: 6px;
      }
      .bf-add:disabled { cursor: default; }
      .bf-add.bf-added { background: #177f4b; opacity: 1; }
      .bf-add-more { margin-top: 14px; }
      .bf-links { display: flex; justify-content: space-between; align-items: center; margin-top: 8px; gap: 8px; }
      .bf-link {
        background: none; border: none; padding: 8px 4px; min-height: 36px; font-family: inherit;
        font-size: 13px; font-weight: 600; color: var(--brand-text, var(--brand-color)); cursor: pointer;
      }
      .bf-link.bf-quiet { color: var(--g500, #666); font-weight: 500; text-decoration: underline; }
      .bf-declined-row {
        display: flex; flex-direction: column; align-items: flex-start; gap: 6px; margin-top: 12px;
        font-size: 13px; color: var(--g600, #555);
      }
      .bf-not-chip {
        display: inline-flex; align-items: center; gap: 6px; min-height: 40px; padding: 8px 14px;
        border: 1.5px solid var(--brand-text, var(--brand-color)); border-radius: 999px; background: #fdf7fc;
        color: var(--brand-text, var(--brand-color)); font-family: inherit; font-size: 13px; font-weight: 600; cursor: pointer;
      }
      .bf-more-toggle {
        display: flex; align-items: center; gap: 6px; margin: 14px auto 0; background: none; border: none;
        min-height: 40px; font-family: inherit; font-size: 13px; font-weight: 600; cursor: pointer;
        color: var(--brand-text, var(--brand-color));
      }
      .bf-chev { display: inline-block; transform: translateY(-3px); transition: transform 0.2s ease; }
      .bf-chev.open { transform: rotate(180deg) translateY(-3px); }
      .bf-more { margin-top: 8px; }
      .bf-chips { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 12px; }
      .bf-chip {
        border: 1px solid var(--border-interactive, #8e8e8e); background: #fff; border-radius: 99px;
        padding: 6px 12px; min-height: 32px; font-family: inherit; font-size: 12px; color: var(--g600, #555); cursor: pointer;
      }
      .bf-chip.on { border-color: var(--brand-text, var(--brand-color)); color: var(--brand-text, var(--brand-color)); font-weight: 600; }
      .bf-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(72px, 1fr)); gap: 12px 8px; }
      .bf-tool {
        display: flex; flex-direction: column; align-items: center; gap: 6px; background: none; border: none;
        padding: 0; font-family: inherit; cursor: pointer; -webkit-tap-highlight-color: transparent;
      }
      .bf-tool:disabled { cursor: default; }
      .bf-tool-img { position: relative; width: 48px; height: 48px; border-radius: 12px; }
      .bf-tool-img img { width: 48px; height: 48px; border-radius: 12px; object-fit: cover; display: block; opacity: 0.8; }
      .bf-tool.on .bf-tool-img img, .bf-tool.has .bf-tool-img img { opacity: 1; }
      .bf-tick {
        position: absolute; top: -4px; inset-inline-end: -4px; width: 17px; height: 17px; border-radius: 50%;
        background: #1a8f55; color: #fff; font-size: 10px; display: flex; align-items: center; justify-content: center;
        border: 2px solid #fff;
      }
      .bf-tool-name { font-size: 11px; font-weight: 600; color: var(--g600, #555); text-align: center; line-height: 1.25; }
      /* Well clear of the tool grid and the links above, so it is not tapped by accident (Danna 26.09). */
      .bf-app { margin-top: 28px; padding-top: 16px; border-top: 1px solid var(--g100, #eceaf2); }
      .bf-note { font-size: 11px; color: var(--g400, #888); line-height: 1.6; text-align: center; margin-top: 12px; }
      .bf-overlay {
        position: fixed; inset: 0; background: rgba(20, 20, 30, 0.45); z-index: 10000; /* above the bottom nav (9000) */
        display: flex; align-items: flex-end; justify-content: center;
      }
      .bf-sheet {
        background: #fff; width: 100%; max-width: 520px; max-height: 88vh; max-height: 88dvh; overflow-y: auto;
        overscroll-behavior: contain; -webkit-overflow-scrolling: touch;
        border-radius: 18px 18px 0 0; padding: 18px 18px calc(18px + env(safe-area-inset-bottom));
      }
      @media (min-width: 600px) {
        .bf-overlay { align-items: center; }
        .bf-sheet { border-radius: 18px; }
      }
      .bf-sheet-head { display: flex; align-items: center; gap: 12px; margin-bottom: 8px; }
      .bf-sec { margin-top: 12px; }
      .bf-sec-t { font-size: 12px; font-weight: 700; color: var(--navy, #1a1a2e); margin-bottom: 2px; }
      .bf-sec p { margin: 0; font-size: 13px; color: var(--g600, #555); line-height: 1.55; }
      .bf-wellness { margin: 14px 0 0; font-size: 12px; color: var(--g500, #666); line-height: 1.55; }
    `,
  ],
})
export class ToolsRecommendationComponent implements OnInit, OnDestroy {
  /** Peritale web: no app, so the tools continue here (Danna 27.09). */
  readonly webLoop = isWebDailyLoop();
  /** results domain key: 'symptoms' | 'think' | 'sense' */
  @Input() domain = '';
  /**
   * Category keys the user actually has data for (e.g. the symptom clusters
   * that emerged from their report). When provided, only these categories are
   * used; omit to use the full matrix.
   */
  @Input() activeCategories?: string[];

  matrix?: DomainMatrix;
  tools: GridTool[] = [];
  best: GridTool | null = null;
  declined = false;
  infoOpen = false;
  private overlayEl?: HTMLElement;

  /**
   * The info sheet lives on <body> while open. Inside the results panel, iOS
   * Safari kept it in the panel's layer, so the page's own dots, disclaimer and
   * bottom nav drew over it and it could not scroll (Danna 23.09).
   */
  @ViewChild('bfOverlay') set bfOverlay(ref: ElementRef<HTMLElement> | undefined) {
    const el = ref?.nativeElement;
    if (el && el.parentElement !== document.body) document.body.appendChild(el);
    this.overlayEl = el;
  }
  moreOpen = false;
  catFilter = '';
  busy = false;
  selected = new Set<string>();
  private active = new Set<string>();
  private topSymptomId: string | null = null;

  constructor(
    private routineStore: RoutineStoreService,
    private routine: RoutineService,
    private rest: RestService,
    private dismissed: DismissedToolsService,
    private translation: TranslationService,
    private route: ActivatedRoute,
  ) {}

  ngOnDestroy(): void {
    this.overlayEl?.remove();
  }

  ngOnInit(): void {
    const base = DOMAIN_MATRIX[this.domain];
    if (!base) return;

    this.matrix =
      this.activeCategories && this.activeCategories.length
        ? { ...base, categories: base.categories.filter((c) => this.activeCategories!.includes(c.key)) }
        : base;
    if (!this.matrix.categories.length) this.matrix = base;

    this.tools = this.matrix.gridIds
      .map((id) => TOOLS_BY_ID[id])
      .filter(Boolean)
      .map((t) => ({
        ...t,
        img: toolImgUrl(t.id),
        cats: this.matrix!.categories.filter((c) => c.recommended.includes(t.id)).map((c) => c.key),
      }));

    this.pickBest();
    void this.loadContext();
  }

  get visibleTools(): GridTool[] {
    const rest = this.tools.filter((t) => t.id !== this.best?.id);
    return this.catFilter ? rest.filter((t) => t.cats.includes(this.catFilter)) : rest;
  }

  mins(id: string): number | undefined {
    return TOOL_BY_ID[id]?.mins;
  }

  inRoutine(id: string): boolean {
    return this.active.has(id);
  }

  /** Something from this section is in her routine. */
  get addedHere(): boolean {
    return this.tools.some((t) => this.active.has(t.id));
  }

  /** Her ratings and her current routine, both optional: the card works without them. */
  private async loadContext(): Promise<void> {
    const scanId = this.findScanId();
    const [ratings, schedule] = await Promise.all([
      scanId
        ? this.rest
            .request<{ data?: RatedSymptom[] }>({
              method: 'GET',
              path: `doctor-report/${scanId}/rated-symptoms`,
              throwError: true,
            })
            .then((r) => r?.data)
            .catch(() => null)
        : Promise.resolve(null),
      this.routine.getSchedule(true).catch(() => ({}) as RoutineSchedule),
    ]);
    this.topSymptomId = topRatedSymptom(ratings)?.id ?? null;
    this.active = new Set(
      Object.entries(schedule || {})
        .filter(([, v]) => !!(v as { active?: boolean })?.active)
        .map(([k]) => k),
    );
    if (!this.declined) this.pickBest();
  }

  private findScanId(): string | null {
    let r: ActivatedRoute | null = this.route;
    while (r) {
      const id = r.snapshot.paramMap.get('scanId');
      if (id) return id;
      r = r.parent;
    }
    return null;
  }

  /**
   * The #1 best fit: a tool she has not declined and is not already doing,
   * preferring the ones that answer her highest-rated symptom, then the ones
   * recommended for this section's categories, in their listed order.
   */
  private pickBest(): void {
    const forHer = this.topSymptomId ? toolsForSymptom(this.topSymptomId) : [];
    const recommended = new Set(this.matrix!.categories.flatMap((c) => c.recommended));
    const open = this.tools.filter((t) => !this.dismissed.has(t.id));
    const rank = (t: GridTool): number => {
      const her = forHer.indexOf(t.id);
      if (her >= 0 && !this.active.has(t.id)) return her;
      if (recommended.has(t.id) && !this.active.has(t.id)) return 100 + this.tools.indexOf(t);
      if (!this.active.has(t.id)) return 200 + this.tools.indexOf(t);
      return 300 + this.tools.indexOf(t);
    };
    this.best = [...open].sort((a, b) => rank(a) - rank(b))[0] ?? null;
  }

  /** A reason only when her own ratings point at this tool. Never a filler.
   *  A getter, so it follows a language switch. */
  get why(): string {
    const tool = this.best;
    if (!tool || !this.topSymptomId || !toolsForSymptom(this.topSymptomId).includes(tool.id)) return '';
    const key = 'doctorReport.symptoms.' + this.topSymptomId;
    const name = this.translation.translate(key);
    if (!name || name === key) return '';
    return this.translation.translate('shell.home.rec.because', { symptom: name });
  }

  decline(): void {
    if (!this.best) return;
    this.dismissed.dismiss(this.best.id);
    this.declined = true;
  }

  undoDecline(): void {
    if (!this.best) return;
    this.dismissed.restore(this.best.id);
    this.declined = false;
  }

  /** A tool already in her routine comes out on a tap; the tile stays in place, unticked. */
  toggle(id: string): void {
    if (this.busy) return;
    if (this.inRoutine(id)) {
      void this.removeIds([id]);
      return;
    }
    if (this.selected.has(id)) this.selected.delete(id);
    else this.selected.add(id);
  }

  async addBest(): Promise<void> {
    if (!this.best || this.busy) return;
    await this.addIds([this.best.id]);
  }

  async addSelected(): Promise<void> {
    if (!this.selected.size || this.busy) return;
    await this.addIds([...this.selected]);
    this.selected.clear();
  }

  private async addIds(ids: string[]): Promise<void> {
    this.busy = true;
    try {
      this.routineStore.add(ids);
      const schedule: RoutineSchedule = { ...(await this.routine.getSchedule(true)) };
      let changed = false;
      for (const id of ids) {
        if (TOOL_BY_ID[id] && !schedule[id]?.active) {
          schedule[id] = { active: true, freq: 'daily', time: 'anytime' };
          changed = true;
        }
      }
      if (changed) await this.routine.saveSchedule(schedule);
      ids.forEach((id) => this.active.add(id));
    } catch (e) {
      console.error('add to routine from results failed:', e);
    } finally {
      this.busy = false;
    }
  }

  async removeIds(ids: string[]): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    try {
      const schedule: RoutineSchedule = { ...(await this.routine.getSchedule(true)) };
      let changed = false;
      for (const id of ids) {
        if (schedule[id]?.active) {
          schedule[id] = { ...schedule[id], active: false };
          changed = true;
        }
      }
      if (changed) await this.routine.saveSchedule(schedule);
      ids.forEach((id) => {
        this.active.delete(id);
        this.routineStore.remove(id);
      });
    } catch (e) {
      console.error('remove from routine from results failed:', e);
    } finally {
      this.busy = false;
    }
  }
}
